import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import {
    AdditionalForMessageM13,
    AdditionalForMessageR,
    AdditionalForMessageR13,
    Body1,
    Body1R,
    Body2,
    Body2B,
    Body3,
    Body3M,
    Body3Uppercase,
    Body4,
    Body4M,
    Body4Text,
    Body5,
    Button,
    Caption1,
    Caption2,
    Caption2R,
    Caption3,
    Caption4,
    Caption5,
    Fat1,
    Message,
    Message14,
    Message14R,
    Message15,
    Message16,
    Message17,
    Message18,
    Message19,
    MessageInput,
    SubCaptionR,
    Subhead2R,
    Subhead3,
    Subtitle1,
    Subtitle2,
    Subtitle3,
    Subtitle4,
    Subtitle5,
    Text1,
    Text2,
    Text3,
    Title1,
    Title2,
    Title3,
    Title4,
    Title42,
    Title5,
    Title5B,
    Title6,
    Title7,
    Title8,
    Title9,
} from './view';

/*
    This component provides the base styles for texts, headings and etc.
    The better way to customize it is to use styled function

    const StyledText = styled(Typography)`
        // new styles here
    `
*/

export type TypoVariants =
    | 'title1'
    | 'title2'
    | 'title3'
    | 'title4'
    | 'title5'
    | 'title5B'
    | 'title6'
    | 'title42'
    | 'title7'
    | 'title8'
    | 'title9'
    | 'body1'
    | 'body1R'
    | 'body2'
    | 'body2B'
    | 'body3'
    | 'body3M'
    | 'Body3Uppercase'
    | 'body4'
    | 'body4M'
    | 'Body4Text'
    | 'body5'
    | 'text1'
    | 'text2'
    | 'text3'
    | 'button'
    | 'subtitle1'
    | 'subtitle2'
    | 'subtitle3'
    | 'subtitle4'
    | 'subtitle5'
    | 'caption1'
    | 'caption2'
    | 'caption2R'
    | 'caption3'
    | 'caption4'
    | 'caption5'
    | 'subCaptionR'
    | 'message'
    | 'message14'
    | 'message15'
    | 'message16'
    | 'message17'
    | 'message18'
    | 'message19'
    | 'message14R'
    | 'messageInput'
    | 'additionalForMessageM13'
    | 'additionalForMessageR'
    | 'additionalForMessageR13'
    | 'subhead2R'
    | 'subhead3'
    | 'fat1';

const COMPONENT_MAPPING = {
    text1: Text1,
    text2: Text2,
    text3: Text3,
    title1: Title1,
    title2: Title2,
    title3: Title3,
    title4: Title4,
    title42: Title42,
    title5: Title5,
    title5B: Title5B,
    title6: Title6,
    title7: Title7,
    title8: Title8,
    title9: Title9,
    body1: Body1,
    body1R: Body1R,
    body2: Body2,
    body2B: Body2B,
    body3: Body3,
    body3M: Body3M,
    Body3Uppercase,
    body4: Body4,
    Body4Text,
    body4M: Body4M,
    body5: Body5,
    button: Button,
    subtitle1: Subtitle1,
    subtitle2: Subtitle2,
    subtitle3: Subtitle3,
    subtitle4: Subtitle4,
    subtitle5: Subtitle5,
    caption1: Caption1,
    caption2: Caption2,
    caption2R: Caption2R,
    caption3: Caption3,
    caption4: Caption4,
    caption5: Caption5,
    subCaptionR: SubCaptionR,
    message: Message,
    message14: Message14,
    message15: Message15,
    message16: Message16,
    message17: Message17,
    message18: Message18,
    message19: Message19,
    message14R: Message14R,
    messageInput: MessageInput,
    additionalForMessageM13: AdditionalForMessageM13,
    additionalForMessageR: AdditionalForMessageR,
    additionalForMessageR13: AdditionalForMessageR13,
    subhead2R: Subhead2R,
    subhead3: Subhead3,
    fat1: Fat1,
};

export type ColorVariants = 'base' | 'second' | 'disabled' | 'colored' | 'danger';

export interface Props extends PropsWithChildren, HTMLAttributes<HTMLElement> {
    variant: TypoVariants;
    margin?: string;
    mobileMargin?: string;
    padding?: string;
    mobilePadding?: string;
    block?: boolean;
    color?: string;
    /**
     * @deprecated Use props 'color' instead. The colors for the text can be any from the entire palette.
     */
    colorVariant?: ColorVariants;
    style?: React.CSSProperties;
    textAlign?: string;
    as?: string;
    textOverflowDisabled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    cy?: string;
}

const Typography = ({
    block,
    variant,
    color = '',
    colorVariant = 'base',
    margin,
    padding,
    mobileMargin,
    mobilePadding,
    textAlign,
    textOverflowDisabled,
    cy,
    ...props
}: Props) => {
    const Component = COMPONENT_MAPPING[variant];

    const typo = useMemo(
        () => (
            <Component
                $block={block}
                $color={color}
                $colorVariant={colorVariant}
                $margin={margin}
                $padding={padding}
                $mobileMargin={mobileMargin}
                $mobilePadding={mobilePadding}
                $textAlign={textAlign}
                $textOverflowDisabled={textOverflowDisabled}
                data-cy={cy}
                {...props}
            />
        ),
        [props, color, colorVariant],
    );

    return typo;
};

export default Typography;
